import { createContext } from 'react';
import type { Destination, TripConfig } from '../types';
import { destinationKey } from './destinationItems';
import { boundaryTransport } from './scheduleTravel';
import { journeyFor } from './journeys';
import { matchesCity } from './placeNames';
import { clipParticipation, resolveTravellerRoute, visitForDate } from '../../functions/src/travellerRoutePolicy.mjs';
import type { Junction } from '../../functions/src/junctionPolicy.mjs';

/** Route metadata lives in private branch documents, never the shared trip. */
export interface ItineraryBranch {
  id: string;
  parentId: string | null;
  title: string;
  memberIds: string[];
  splitVisitId: string;
  rejoinVisitId: string | null;
  rejoinDate: string;
  destinations: Destination[];
  ownerId?: string;
  revision?: number;
  /** Safe split/rejoin events (R8), on a top-level branch's trip-wide summary. */
  junctions?: Junction[];
  /** Bookings belong to this route's private storage scope. */
  accommodations?: TripConfig['accommodations'];
  itinerary?: TripConfig['itinerary'];
}

/** Offer personal filtering only when current travellers have different plans.
 * Use already-authorised content and safe branch membership, never extra reads. */
export function hasTravellerDifferences(trip: TripConfig, branches: ItineraryBranch[], configs: Record<string, TripConfig> = {}): boolean {
  const members = trip.memberIds;
  if (!members.length) return false;
  if (members.length > 1 && members.some(id => trip.participation?.[id])) return true;
  const subset = (ids?: string[]) => !!ids && members.some(id => ids.includes(id)) && members.some(id => !ids.includes(id));
  if (branches.some(branch => subset(branch.memberIds))) return true;
  // An empty assignment still differs from All: the last opt-out must not
  // remove Just me and put the booking back into that traveller's view.
  const assigned = (item?: { participantIds?: string[] }) => !!item?.participantIds && members.some(id => !item.participantIds!.includes(id));
  return [trip, ...branches, ...Object.values(configs)].some(scope =>
    scope.accommodations?.some(assigned) || Object.values(scope.itinerary ?? {}).some(day =>
      assigned(day.accommodation) || (['accommodations', 'transports', 'attractions', 'eats', 'nightlife', 'shopping'] as const)
        .some(key => day[key]?.some(assigned))));
}

export function branchAncestors(branches: ItineraryBranch[], id: string): ItineraryBranch[] {
  const result: ItineraryBranch[] = [];
  let branch = branches.find(b => b.id === id);
  while (branch) {
    if (result.some(b => b.id === branch!.id)) return [];
    result.unshift(branch);
    if (branch.parentId === null) return result;
    branch = branches.find(b => b.id === branch!.parentId);
  }
  return [];
}

export function canEnterBranch(branches: ItineraryBranch[], id: string, userId: string) {
  const trail = branchAncestors(branches, id);
  return trail.length > 0 && trail.every(b => b.memberIds.includes(userId));
}

export function validateBranch(branch: ItineraryBranch, branches: ItineraryBranch[], trip: TripConfig): string {
  if (!branch.title.trim()) return 'Give this branch a name.';
  if (!branch.memberIds.length || branch.memberIds.some(id => !trip.memberIds.includes(id))) return 'Select travellers from this trip.';
  const parent = branch.parentId ? branches.find(b => b.id === branch.parentId) : null;
  if (branch.parentId && (!parent || branchAncestors(branches, parent.id).some(b => b.id === branch.id))) return 'Choose a valid parent route.';
  if (parent && branch.memberIds.some(id => !parent.memberIds.includes(id))) return 'A nested branch must include travellers from its parent route.';
  const stops = parent?.destinations ?? trip.destinations;
  const split = stops.find(d => destinationKey(d) === branch.splitVisitId);
  const rejoin = stops.find(d => destinationKey(d) === branch.rejoinVisitId);
  if (!split || (branch.rejoinVisitId && !rejoin)) return 'Choose where this branch leaves and rejoins.';
  if (branches.some(b => b.id !== branch.id && b.parentId === branch.parentId && b.splitVisitId === branch.splitVisitId
    && b.memberIds.some(id => branch.memberIds.includes(id)))) return 'A selected traveller already branches from this stop. Edit that branch or choose another departure stop.';
  if (!branch.destinations.length) return 'Add at least one destination.';
  const validDate = (date: string) => /^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(Date.parse(`${date}T12:00:00Z`)) && new Date(`${date}T12:00:00Z`).toISOString().slice(0, 10) === date;
  const ordered = [...branch.destinations].sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));
  if (ordered.some(d => !d.name.trim() || !validDate(d.arrivalDate) || !validDate(d.departureDate) || d.arrivalDate >= d.departureDate)) return 'Enter a place and valid arrival/departure dates for each stop.';
  if (ordered.some((d, i) => i > 0 && d.arrivalDate < ordered[i - 1].departureDate)) return 'Destinations on the same branch cannot overlap.';
  if (rejoin && (!validDate(branch.rejoinDate) || branch.rejoinDate < rejoin.arrivalDate || branch.rejoinDate > rejoin.departureDate || ordered.at(-1)!.departureDate > branch.rejoinDate)) return 'Choose a rejoin date within that destination, after this branch finishes.';
  if (rejoin && stops.indexOf(rejoin) < stops.indexOf(split)) return 'Rejoin at this destination or a later stop on the route.';
  const overlaps = branches.some(b => b.id !== branch.id && b.parentId === branch.parentId && b.memberIds.some(id => branch.memberIds.includes(id))
    && b.destinations.some(d => ordered.some(other => d.arrivalDate < other.departureDate && d.departureDate > other.arrivalDate)));
  if (overlaps) return 'A selected traveller already has a branch on these dates. Adjust the dates or travellers.';
  const child = branches.find(b => b.parentId === branch.id && b.memberIds.some(id=>trip.memberIds.includes(id)) && (b.memberIds.some(id => trip.memberIds.includes(id) && !branch.memberIds.includes(id)) || !branch.destinations.some(d => destinationKey(d) === b.splitVisitId) || (b.rejoinVisitId && !branch.destinations.some(d => destinationKey(d) === b.rejoinVisitId))));
  if (child) return 'Update the nested branch before removing its travellers or destinations.';
  return '';
}

export function personalDestinations(trip: TripConfig, branches: ItineraryBranch[], userId: string, parentId: string | null = null): Destination[] {
  return clipParticipation(resolveTravellerRoute(trip.destinations,branches.filter(b=>canEnterBranch(branches,b.id,userId)),userId,parentId), trip.destinations, trip.participation?.[userId]).map(row=>row.visit);
}

export interface PersonalRouteVisit { source:TripConfig; visit:Destination; sourceVisit:Destination; scheduleId:string }
/** Null means private content is not authorised/loaded yet, never "use main". */
export function personalRouteVisits(trip:TripConfig,branches:ItineraryBranch[],configs:Record<string,TripConfig>,userId:string):PersonalRouteVisit[]|null {
  if(branches.some(b=>b.memberIds.includes(userId) && !configs[b.id])) return null;
  const available=branches.filter(b=>canEnterBranch(branches,b.id,userId));
  return clipParticipation(resolveTravellerRoute(trip.destinations,available,userId), trip.destinations, trip.participation?.[userId]).map(row=>({
    source:row.scopeId?configs[row.scopeId]:trip,visit:row.visit,sourceVisit:row.sourceVisit,
    scheduleId:row.sourceVisit.arrivalDate.slice(0,10),
  }));
}

export function projectBranchTrip(trip: TripConfig, destinations: Destination[], includeLastDay = false): TripConfig {
  if (destinations === trip.destinations) return trip;
  const sorted = [...destinations].sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));
  const sharedVisits = sorted.filter(d => trip.destinations.some(original => destinationKey(original) === destinationKey(d)));
  const sharedDate = (date: string) => {
    const owner = visitForDate(trip.destinations, date);
    return sharedVisits.some(d => (!owner || destinationKey(owner) === destinationKey(d)) && date >= d.arrivalDate
      && (date < d.departureDate || ((date === trip.endDate || includeLastDay && date === sorted.at(-1)?.departureDate) && date === d.departureDate)));
  };
  // Until item assignment/storage is implemented, never project main-route
  // bookings into an alternate destination merely because its dates overlap.
  return { ...trip, destinations: sorted,
    itinerary: Object.fromEntries(Object.entries(trip.itinerary ?? {}).filter(([date]) => sharedDate(date))),
    accommodations: (trip.accommodations ?? []).filter(a => sharedVisits.some(d => a.checkIn.slice(0,10) < d.departureDate && a.checkOut.slice(0,10) > d.arrivalDate)),
    startDate: sorted[0]?.arrivalDate || trip.startDate, endDate: sorted.at(-1)?.departureDate || trip.endDate };
}

/** A personal route keeps each contiguous section in its original storage
 * scope. Never flatten private bookings/ledgers into the root trip. */
export function personalRouteSections(trip:TripConfig, branches:ItineraryBranch[], configs:Record<string,TripConfig>, userId:string) {
  const visits=personalRouteVisits(trip,branches,configs,userId);
  if(!visits) return null;
  const sections:{source:TripConfig;destinations:Destination[]}[]=[];
  for(const {source,visit:destination} of visits) {
    const last=sections.at(-1);
    if(last?.source.id===source.id) last.destinations.push(destination);
    else sections.push({source,destinations:[destination]});
  }
  return sections.map(section=>{
    const view=projectBranchTrip(section.source,section.destinations, !!trip.participation?.[userId] && section === sections.at(-1));
    const legs=Object.entries(section.source.itinerary ?? {}).flatMap(([date,day])=>(day.transports ?? []).map(t=>({...t,_dayDate:date})));
    const atBoundary=legs.filter(t=>section.destinations.some(d=>boundaryTransport(d,[t],'arrival') || boundaryTransport(d,[t],'departure')));
    const boundary=new Set(atBoundary.flatMap(t=>journeyFor(t,legs)).map(t=>t.id));
    for(const [date,day] of Object.entries(section.source.itinerary ?? {})) {
      if(view.itinerary?.[date]) continue;
      const transports=(day.transports ?? []).filter(t=>boundary.has(t.id));
      if(transports.length) view.itinerary={...view.itinerary,[date]:{date,transports}};
    }
    return {...section,view:forTraveller(view,userId)};
  });
}

/** Just me: hide items assigned only to other people. A view filter, never
 * an access rule; saves merge per item, so hidden items are left untouched. */
function forTraveller(view:TripConfig,userId:string):TripConfig {
  const mine=<T,>(items:T[]|undefined)=>items?.filter(item=>{const ids=(item as {participantIds?:string[]}).participantIds; return !ids || ids.includes(userId);});
  return {...view,accommodations:mine(view.accommodations),
    itinerary:Object.fromEntries(Object.entries(view.itinerary ?? {}).map(([date,day])=>[date,{...day,
      ...(day.accommodation ? {accommodation:mine([day.accommodation])?.[0]} : {}),
      ...Object.fromEntries((['attractions','eats','nightlife','shopping','transports','accommodations'] as const)
        .filter(key=>Array.isArray(day[key])).map(key=>[key,mine(day[key] as unknown[])]))}]))};
}

export interface PersonalHomeRoute { config:TripConfig; visits:PersonalRouteVisit[] }
const byId = <T extends {id?:string}>(items:T[]) => items.filter((item,i)=>!item.id || items.findIndex(other=>other.id===item.id)===i);
/** Home's read-only view of one traveller's continuous route. Never save it:
 * money, travellers and writes stay on the root (or the item's own scope).
 * undefined = not in a branch, use the root; null = private content loading. */
export function personalHomeRoute(trip:TripConfig, branches:ItineraryBranch[], configs:Record<string,TripConfig>, userId:string):PersonalHomeRoute|null|undefined {
  if(!branches.some(b=>b.memberIds.includes(userId)) && !trip.participation?.[userId]) return undefined;
  const visits=personalRouteVisits(trip,branches,configs,userId);
  const sections=personalRouteSections(trip,branches,configs,userId);
  if(!visits || !sections) return null;
  const itinerary:NonNullable<TripConfig['itinerary']>={};
  for(const {view} of sections) for(const [date,day] of Object.entries(view.itinerary ?? {})) {
    const prev=itinerary[date] as Record<string,unknown>|undefined;
    itinerary[date]=prev ? Object.fromEntries([...new Set([...Object.keys(prev),...Object.keys(day)])].map(key=>{
      const a=prev[key], b=(day as Record<string,unknown>)[key];
      return [key, Array.isArray(a) && Array.isArray(b) ? byId([...a,...b]) : b ?? a];
    })) as typeof day : day;
  }
  const destinations=visits.map(v=>v.visit), first=visits[0], last=visits.at(-1);
  // A shared first/last stop keeps the trip's own outbound/return travel days.
  const startDate=!first ? trip.participation?.[userId]?.startDate ?? trip.startDate : first.source===trip && !trip.participation?.[userId] && trip.startDate<first.visit.arrivalDate ? trip.startDate : first.visit.arrivalDate;
  const endDate=!last ? trip.participation?.[userId]?.endDate ?? trip.endDate : last.source===trip && !trip.participation?.[userId] && trip.endDate>last.visit.departureDate ? trip.endDate : last.visit.departureDate;
  return { visits, config:{ ...trip, destinations, itinerary, startDate, endDate,
    accommodations:byId(sections.flatMap(s=>s.view.accommodations ?? [])) } };
}

/** Which route visit owns an item (searched in each visit's own source) or a date; -1 if none. */
export function routeVisitIndex(visits:PersonalRouteVisit[], target:{itemId?:string;date?:string}):number {
  if(target.date) {
    const owner=visitForDate(visits.map(v=>v.visit),target.date);
    return visits.findIndex(v=>v.visit===owner);
  }
  const owns=(v:PersonalRouteVisit,date:string)=>!!date && visitForDate(v.source.destinations ?? [],date.slice(0,10))===v.sourceVisit;
  return visits.findIndex(v=>(v.source.accommodations ?? []).some(a=>a.id===target.itemId && owns(v,a.checkIn ?? ''))
    || Object.entries(v.source.itinerary ?? {}).some(([date,day])=>owns(v,date)
      && Object.values(day).some(items=>Array.isArray(items) && items.some((item:{id?:string})=>item?.id===target.itemId))));
}

const dayEntry = (date:string) => { const t=new Date(`${date}T00:00:00Z`);
  return { date, label:t.toLocaleDateString('en-GB',{day:'numeric',month:'short',timeZone:'UTC'}), dow:t.toLocaleDateString('en-GB',{weekday:'short',timeZone:'UTC'}) }; };
/** A route visit's Schedule days and its same-scope neighbours. A changeover
 * into another scope belongs to the arriving stop (visitForDate), so it is
 * never planned in, or shared through, this visit's schedule doc. */
export function routeScheduleVisit(visits:PersonalRouteVisit[], idx:number) {
  const here=visits[idx], prev=visits[idx-1], next=visits[idx+1];
  const same=(other?:PersonalRouteVisit)=>other && other.source.id===here.source.id ? other.sourceVisit : undefined;
  const dates:ReturnType<typeof dayEntry>[]=[];
  for(let d=here.visit.arrivalDate; d<here.visit.departureDate && dates.length<60; d=addUtcDay(d)) dates.push(dayEntry(d));
  if(!next || same(next)) dates.push(dayEntry(here.visit.departureDate));
  // Context, not a schedule link: only disclose an origin to a private scope
  // whose entire membership can read it. Never copy a private origin to root.
  const origin = prev && !same(prev) && here.source.branchId
    && prev.visit.departureDate === here.visit.arrivalDate
    && here.source.memberIds.length > 0 && here.source.memberIds.every(id => prev.source.memberIds.includes(id)) ? prev.visit : undefined;
  const arrivalFrom = origin ? { name: origin.name, country: origin.country,
    arrivalDate: origin.arrivalDate, departureDate: origin.departureDate } : undefined;
  return { dates:dates.length ? dates : [dayEntry(here.visit.arrivalDate)], prev:same(prev), next:same(next), visit:here.visit, arrivalFrom };
}
const addUtcDay=(date:string)=>{ const t=new Date(`${date}T00:00:00Z`); t.setUTCDate(t.getUTCDate()+1); return t.toISOString().slice(0,10); };

type LegEnds = { from?: string; fromCity?: string; to?: string; toCity?: string; dep?: string; arr?: string; isRental?: boolean; participantIds?: string[] };
/** The private branch a new leg belongs to for this traveller: one leaving the
 * shared route, returning to it, or moving within a branch. The deepest branch
 * wins (a nested split is the nested branch's). Never inferred from the shared
 * endpoint alone; undefined means it isn't a branch leg. */
export function boundaryScope(leg:LegEnds, visits:PersonalRouteVisit[]):TripConfig|undefined {
  if (leg.isRental) return undefined;
  const ends=(places:(string|undefined)[],at?:string)=>!at ? [] : visits.filter(v=>
    places.some(p=>matchesCity(p,v.visit.name)) && at.slice(0,10)>=v.visit.arrivalDate && at.slice(0,10)<=v.visit.departureDate);
  const depth=(source:TripConfig)=>{ let n=0, parent=(source as {parentId?:string|null}).parentId;
    while(parent && n<10){n++;parent=(visits.find(v=>v.source.branchId===parent)?.source as {parentId?:string|null}|undefined)?.parentId;}
    return n; };
  return [...ends([leg.fromCity,leg.from],leg.dep),...ends([leg.toCity,leg.to],leg.arr)].map(v=>v.source)
    .filter(source=>source.branchId && (!leg.participantIds || leg.participantIds.some(id=>source.memberIds.includes(id))))
    .sort((a,b)=>depth(b)-depth(a))[0];
}

/** The signed-in traveller's route on the active trip (App provides it), for
 * places that must not infer root ownership, like booking import. */
export const PersonalRouteContext = createContext<PersonalRouteVisit[] | undefined>(undefined);

/** "Fri, 4 June" for split/rejoin rails. */
export function junctionDate(date:string):string {
  const t=new Date(`${date}T12:00:00Z`);
  return `${t.toLocaleDateString('en-GB',{weekday:'short',timeZone:'UTC'})}, ${t.toLocaleDateString('en-GB',{day:'numeric',month:'long',timeZone:'UTC'})}`;
}

/** Date-only itinerary rows for one branch and direction. Schedule retains the
 * original timed events; never combine rows across different branch links. */
export function branchJunctionRows(branch: ItineraryBranch, events: Junction[], kind: Junction['kind']): { date: string; ids: string[] }[] {
  const dated = events.filter(event => event.kind === kind);
  if (!dated.length) return [{
    date: kind === 'rejoin' ? branch.rejoinDate : branch.destinations.map(d => d.arrivalDate).filter(Boolean).sort()[0] || '',
    ids: [...branch.memberIds],
  }];
  const byDate = new Map<string, Set<string>>();
  for (const event of dated) {
    const people = byDate.get(event.date) ?? new Set<string>();
    event.participantIds.forEach(id => people.add(id));
    byDate.set(event.date, people);
  }
  return [...byDate].sort(([a], [b]) => a.localeCompare(b)).map(([date, ids]) => ({ date, ids: [...ids] }));
}

/** Place a safe, date-only junction relative to its shared destination stay. */
export function junctionPosition(date:string, destination:Pick<Destination,'arrivalDate'|'departureDate'>,
  fallback:'before'|'after'):'before'|'during'|'after' {
  if (!date || !destination.arrivalDate || !destination.departureDate) return fallback;
  if (date <= destination.arrivalDate) return 'before';
  if (date >= destination.departureDate) return 'after';
  return 'during';
}

/** Presentation only: private branches may still extend beyond the root route. */
export function junctionLabel(kind: Junction['kind'], rootBoundary?: 'start' | 'end', firstJoin = false): string | null {
  if (kind === 'split') return rootBoundary === 'start' ? null : 'Splitting off';
  if (rootBoundary === 'end') return null;
  return rootBoundary === 'start' || firstJoin ? 'Joining' : 'Rejoining';
}

/** Safe events keyed by the scope whose route holds their anchors: the root
 * gets top-level branches' (from summaries), a branch its nested children's. */
export function junctionsByScope(tripId:string, branches:ItineraryBranch[], configs:Record<string,TripConfig>):Record<string,Junction[]> {
  const out:Record<string,Junction[]>={};
  const top=branches.flatMap(b=>b.junctions ?? []);
  if(top.length) out[tripId]=top;
  for(const config of Object.values(configs)) {
    const nested=Object.values((config as {childJunctions?:Record<string,Junction[]>}).childJunctions ?? {}).flat();
    if(nested.length) out[config.id]=nested;
  }
  return out;
}
export const JunctionContext = createContext<Record<string, Junction[]>>({});

/** Every branch this traveller may know about: top-level summaries (trip-wide),
 * their own branch documents (so a nested branch they're in is listed — and
 * routes wait for it — before its content loads), and nested summaries from
 * parent branches they can read. A loaded config fills in its full fields. */
export function branchList(summaries:ItineraryBranch[], memberBranches:ItineraryBranch[], configs:Record<string,TripConfig>):ItineraryBranch[] {
  const all=new Map<string,ItineraryBranch>();
  const nested=Object.values(configs).flatMap(c=>(c as {childSummaries?:ItineraryBranch[]}).childSummaries ?? []);
  for(const b of [...summaries,...memberBranches,...nested]) if(!all.has(b.id)) all.set(b.id,b);
  return [...all.values()].map(b=>configs[b.id] ? {...b,...configs[b.id],id:b.id} as ItineraryBranch : b);
}

/** Who is away on a private branch that day, from safe split/rejoin events
 * only: from their split date until (not including) their rejoin date. */
export function awayOn(events:Junction[], date:string):string[] {
  return [...new Set(events.filter(e=>e.kind==='split' && e.date<=date).flatMap(split=>split.participantIds.filter(id=>
    !events.some(e=>e.kind==='rejoin' && e.participantIds.includes(id) && e.date>=split.date && e.date<=date))))];
}
